import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties, ComponentProps, ReactNode } from 'react';
import { useState } from 'react';
import type { To } from 'react-router';
import styled from 'styled-components';

import {
  DivisionChange,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS } from '@sorare/core/src/atoms/typography';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { LineupCardsLayout } from '@sorare/core/src/components/lineup/LineupCardsLayout';
import type { LineupStyle } from '@sorare/core/src/components/lobby/ContenderLayout';
import { ContenderLayout } from '@sorare/core/src/components/lobby/ContenderLayout';
import { Variant } from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { RewardMultiplier } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardMultiplier';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { range } from '@sorare/core/src/lib/arrays';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { ContenderDrawer } from 'components/contender/ContenderDrawer';
import { ContenderDrawerTabs } from 'components/contender/ContenderDrawer/types';
import { usePreferredLineupStyle } from 'hooks/usePreferredLineupStyle';
import { LineupLayoutHeader } from 'layouts/LineupLayout/LineupLayoutHeader';
import { SimpleContenderFooterStatus } from 'layouts/LineupLayout/LiveLineupFooter/LeftLiveLineupFooter/SimpleContenderFooterStatus';
import { isFixtureOpened } from 'lib/so5';

import { SimpleCardPlaceholder } from '../SimpleCardPlaceholder';
import { So5Lineup } from '../So5Lineup';
import { ContenderFooter } from './ContenderFooter';
import { ContenderLiveProjectedRanking } from './ContenderLiveProjectedRanking';
import { DivisionChangeBanner } from './DivisionChangeBanner';
import type { Contender_so5LeaderboardContender } from './__generated__/index.graphql';

const divisionChangeColor: Record<DivisionChange, string> = {
  [DivisionChange.PROMOTED]: 'var(--c-rgb-onBase-success)',
  [DivisionChange.RELEGATED]: 'var(--c-rgb-onBase-error)',
  [DivisionChange.STABLE]: '200, 200, 200',
};

const getColors = ({
  divisionChange,
  withBoostedRewards,
}: {
  divisionChange?: DivisionChange;
  withBoostedRewards: boolean;
}) => {
  if (divisionChange) {
    return divisionChangeColor[divisionChange];
  }
  if (withBoostedRewards) {
    return '252, 255, 99';
  }
  return null;
};

const Anchor = styled.div`
  height: 100%;

  /**
  * --current-stack-height doesn't work here because the hash scroll happens
  * before the measure has time to finish and updating scroll-margin-top value
  * after the scroll happened is a no-op
  */
  max-width: 100%;
  scroll-margin-top: calc(var(--navbar-height-mobile) + 60px + 44px);
  @media ${desktopAndAbove} {
    scroll-margin-top: calc(var(--navbar-height-desktop) + 88px + 44px);
  }
`;
const Root = styled(Bezel)`
  height: 100%;
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  gap: var(--unit);

  &.inline,
  &.forced_inline {
    background:
      var(--background-gradient),
      linear-gradient(
          var(--c-black) 0%,
          rgba(var(--c-rgb-black), 0.4) 50%,
          var(--c-black) 100%
        )
        padding-box,
      var(--background-url);

    background-size: cover;
    background-position: center;
    background-origin: border-box;
  }

  &.spread {
    background: var(--background-gradient),
      linear-gradient(var(--c-black) 0%, transparent 50%, var(--c-black) 100%),
      radial-gradient(75% 75% at 50% 50%, transparent 50%, var(--c-black) 100%),
      var(--background-url);
    background-size: cover, cover, cover, cover;
    background-position:
      center,
      center,
      center 25px,
      center;
    background-origin: border-box;
  }
`;

const SlotFooter = styled.div`
  display: grid;
  grid-template-rows: var(--triple-unit) var(--double-and-a-half-unit);
  gap: var(--half-unit);
  justify-items: center;
`;

const OPTIMISTIC_SLOTS_COUNT_PER_SPORT: Record<Sport, number> = {
  [Sport.FOOTBALL]: 5,
  [Sport.NBA]: 5,
  [Sport.BASEBALL]: 7,
};
const Separator = styled.div`
  &:first-child,
  &:last-child {
    display: none;
  }
  width: 1px;
  height: var(--double-unit);
  background-color: var(--c-nd-150);
`;

const HorizontalHiddenIfEmpty = styled(Horizontal).attrs({
  gap: 0.5,
  as: BrandXXS,
})`
  color: var(--c-nd-600);
  &:empty {
    display: none;
  }
`;

type Props = {
  anchorId?: string;
  so5LeaderboardContender: Nullable<Contender_so5LeaderboardContender>;
  to?: To;
  onClick?: () => void;
  lineupStyle?: LineupStyle;
  skeletonOptions?: {
    withButtons?: boolean;
    displayBanner?: boolean;
  };
  customFooter?: ReactNode;
  hideSubFooter?: boolean;
  onLineupUpdated?: () => void;
} & Pick<ComponentProps<typeof So5Lineup>, 'fillHeightPercent'> &
  Pick<ComponentProps<typeof ContenderDrawer>, 'onClickGame'> &
  Pick<ComponentProps<typeof LineupLayoutHeader>, 'onLineupUpdated'>;

export const Contender = ({
  anchorId,
  so5LeaderboardContender,
  to,
  onClick,
  lineupStyle: lineupStyleFromProps,
  fillHeightPercent,
  skeletonOptions,
  onClickGame,
  customFooter,
  hideSubFooter,
  onLineupUpdated,
}: Props) => {
  const { sport } = useSportContext();
  const { isMe } = useCurrentUserContext();

  const [drawerContender, setDrawerContender] = useState<string>();
  const [drawerTab, setDrawerTab] = useState<ContenderDrawerTabs>();
  const [lineupStyle] = usePreferredLineupStyle();
  const lineupStyleToApply = lineupStyleFromProps || lineupStyle;

  if (!so5LeaderboardContender) {
    const slotsCount = OPTIMISTIC_SLOTS_COUNT_PER_SPORT[sport];

    return (
      <Anchor id={anchorId}>
        <Root>
          <ContenderLayout
            lineupStyle={lineupStyleToApply}
            withButtons={skeletonOptions?.withButtons}
            header={
              <Horizontal>
                <Skeleton
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                  }}
                />
                <Vertical gap={0.5}>
                  <Skeleton
                    style={{
                      width: '90px',
                      height: '14px',
                      borderRadius: '8px',
                    }}
                  />
                  <Skeleton
                    style={{
                      width: '130px',
                      height: '14px',
                      borderRadius: '8px',
                    }}
                  />
                </Vertical>
              </Horizontal>
            }
            banner={!!skeletonOptions?.displayBanner && <div />}
            footer={
              <Skeleton
                style={{
                  width: '100%',
                  height: skeletonOptions?.withButtons ? '40px' : '100%',
                }}
              />
            }
            subFooter={null}
          >
            <LineupCardsLayout
              fillHeightPercent={fillHeightPercent}
              lineupStyle={lineupStyleToApply}
              slots={slotsCount}
            >
              {range(slotsCount).map(i => (
                <Vertical key={i}>
                  <SimpleCardPlaceholder
                    icon={null}
                    label=""
                    loading={!so5LeaderboardContender}
                  />
                  <SlotFooter>
                    <Skeleton
                      style={{
                        width: '32px',
                        height: '24px',
                        borderRadius: '3em',
                      }}
                    />
                    <Skeleton
                      style={{
                        width: '48px',
                        height: '20px',
                        borderRadius: '8px',
                      }}
                    />
                  </SlotFooter>
                </Vertical>
              ))}
            </LineupCardsLayout>
          </ContenderLayout>
        </Root>
      </Anchor>
    );
  }

  const { so5Leaderboard, so5Lineup } = so5LeaderboardContender;
  const { stadiumUrl, so5LeaderboardGroupByCompetition } = so5Leaderboard;
  const { so5Rankings } = so5Lineup || {};

  const ranking = so5Rankings?.at(0);

  const showProjectedRankingSubFooter = !!ranking?.liveProjectedRanking;
  const showRewardsSubFooter = !!so5Lineup && so5Lineup.rewardMultiplier > 1;

  const color = getColors({
    divisionChange: ranking?.eligibleDivisionChange || undefined,
    withBoostedRewards: so5Leaderboard.roomWithBoostedRewards,
  });

  return (
    <>
      <Anchor id={anchorId}>
        <Root
          borderColor={
            color
              ? `linear-gradient(to bottom, rgba(${color}, 1), var(--c-nd-100) var(--triple-unit), var(--c-nd-100) calc(100% - var(--triple-unit)), rgba(${color}, 1))`
              : 'var(--c-nd-150)'
          }
          className={classNames(lineupStyleToApply)}
          style={
            {
              '--background-url': `url(${proxyUrl(
                (lineupStyleToApply === 'spread'
                  ? stadiumUrl
                  : so5LeaderboardGroupByCompetition.tileBackgroundUrl) || '',
                { cropWidth: 320 }
              )})`,
              '--background-gradient': color
                ? `
                linear-gradient(
                  to bottom,
                  rgba(${color}, 0.2),
                  transparent var(--quadruple-unit),
                  transparent calc(100% - var(--triple-unit)),
                  rgba(${color}, 0.2)
                )
              `
                : 'linear-gradient(rgba(0, 0, 0, 0))',
            } as CSSProperties
          }
        >
          <ContenderLayout
            lineupStyle={lineupStyleToApply}
            withButtons={isFixtureOpened(so5Leaderboard.so5Fixture)}
            withSubFooter={
              showProjectedRankingSubFooter || showRewardsSubFooter
            }
            header={
              <LineupLayoutHeader
                small
                so5LeaderboardContender={so5LeaderboardContender}
                to={to}
                disableLineupViewSelector={!!lineupStyleFromProps}
                onClick={onClick}
                onLineupUpdated={onLineupUpdated}
              />
            }
            banner={
              !!ranking?.eligibleDivisionChange && (
                <DivisionChangeBanner
                  divisionChange={ranking.eligibleDivisionChange}
                />
              )
            }
            footer={
              <ContenderFooter
                showButtons={
                  !so5LeaderboardContender.so5Lineup ||
                  isMe(so5LeaderboardContender.so5Lineup?.user)
                }
                so5LeaderboardContender={so5LeaderboardContender}
                onClickPrizePool={() => {
                  setDrawerTab(ContenderDrawerTabs.PRIZE_POOL);
                  setDrawerContender(so5LeaderboardContender.slug);
                }}
                onClickBonus={() => {
                  setDrawerTab(ContenderDrawerTabs.BONUS);
                  setDrawerContender(so5LeaderboardContender.slug);
                }}
                customFooter={customFooter}
              />
            }
            subFooter={
              !hideSubFooter && (
                <HorizontalHiddenIfEmpty gap={1} center>
                  {!!showProjectedRankingSubFooter && (
                    <ContenderLiveProjectedRanking so5Ranking={ranking} />
                  )}
                  <SimpleContenderFooterStatus
                    so5LeaderboardContender={so5LeaderboardContender}
                    variant="SECONDARY"
                  />
                  {!!showRewardsSubFooter && (
                    <>
                      <Separator />
                      <RewardMultiplier
                        lineup={so5Lineup}
                        variant={Variant.TEXT}
                      />
                    </>
                  )}
                </HorizontalHiddenIfEmpty>
              )
            }
          >
            <So5Lineup
              fillHeightPercent={fillHeightPercent}
              so5LeaderboardContender={so5LeaderboardContender}
              lineupStyle={lineupStyleToApply}
            />
          </ContenderLayout>
        </Root>
      </Anchor>
      {!!drawerContender &&
        drawerContender === so5LeaderboardContender.slug &&
        !!drawerTab && (
          <ContenderDrawer
            open
            onClose={() => {
              setDrawerContender(undefined);
              setDrawerTab(undefined);
            }}
            tab={drawerTab}
            onChangeTab={setDrawerTab}
            contender={drawerContender}
            onClickGame={onClickGame}
          />
        )}
    </>
  );
};

Contender.fragments = {
  so5LeaderboardContender: gql`
    fragment Contender_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        slug
        stadiumUrl
        roomWithBoostedRewards
        so5Fixture {
          slug
          ...isFixtureOpened_so5Fixture
        }
        so5LeaderboardGroupByCompetition: so5LeaderboardGroup(
          groupType: COMPETITION
        ) {
          slug
          tileBackgroundUrl
        }
      }
      so5Lineup {
        id
        rewardMultiplier
        so5Rankings {
          id
          eligibleDivisionChange
          liveProjectedRanking
          ...ContenderLiveFooter_so5Ranking
        }
        user {
          slug
        }
        ...RewardMultiplier_lineup
      }
      ...So5Lineup_so5LeaderboardContender
      ...LineupLayoutHeader_so5LeaderboardContender
      ...ContenderFooter_so5LeaderboardContender
      ...SimpleContenderFooterStatus_so5LeaderboardContender
    }
    ${So5Lineup.fragments.so5LeaderboardContender}
    ${LineupLayoutHeader.fragments.so5LeaderboardContender}
    ${ContenderFooter.fragments.so5LeaderboardContender}
    ${isFixtureOpened.fragments.so5Fixture}
    ${ContenderLiveProjectedRanking.fragments.so5Ranking}
    ${RewardMultiplier.fragments.lineup}
    ${SimpleContenderFooterStatus.fragments.so5LeaderboardContender}
  ` as TypedDocumentNode<Contender_so5LeaderboardContender>,
};
